import React, { useState, useEffect } from 'react';
import { Card, Button, Dropdown, Alert, Container, Spinner } from 'react-bootstrap';
import { useAccount } from 'wagmi';
import Post from './Items/Post.tsx';
import ReviewSubmissionModal from './FormModals/ReviewSubmissionModal.tsx';
import PostSubmissionModal from './FormModals/PostSubmissionModal.tsx';
import { readContract } from '@wagmi/core';
import postConsensusContractABI from '../contracts/tgetherPostConsensus.json';
import postsContractABI from '../contracts/tgetherPosts.json';
import * as config from '../config.tsx';
import ReviewCard from './Items/ReviewCard.tsx';

const postConsensusContractAddress = process.env.REACT_APP_POSTS_CONSENSUS_CONTRACT_ADDRESS as `0x${string}`;
const postsContractAddress = process.env.REACT_APP_POSTS_CONTRACT_ADDRESS as `0x${string}`;

const TypeMap ={
  0:  "NotProcessed", 1: "Pending", 2:"Accepted", 3:"Rejected" }

interface SubmissionType {
  id: number;
  communityName: string;
  consensusType: string;
  consensusTime: number;
  timestamp: number;
}

interface PostType {
  tokenId: string;
  title: string;
  authorAddress: string;
  authorName: string;
  content: string;
  description?: string; // Optional property
}

interface ReviewType {
    reviewId: number;
    member: string;
    content: string;
    consensus: number;        // Numeric consensus (may represent a status or type)
    consensusType: string;
    creds: number;
    afterConsensus: string;  // Directly use boolean here
  }
  
function PostIdSearch({ value }: { value: string }) {
  const { address: account } = useAccount();
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionType | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [communitySubmissions, setCommunitySubmissions] = useState<SubmissionType[]>([]);
  const [posts, setPosts] = useState<PostType[]>([]);
  const [reviews, setReviews] = useState<ReviewType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [communityMap, setCommunityMap] = useState<Map<string, number>>(new Map()); // Store community names with their IDs

  useEffect(() => {
    if (value) {
      fetchPostAndSubmissions(Number(value));
    }
  }, [value]);

  useEffect(() => {
    if (selectedSubmission) {
      fetchReviewsForSubmission(selectedSubmission.id);
    }
  }, [selectedSubmission]);

  const fetchPostAndSubmissions = async (postId: number) => {
    setLoading(true);
    setError('');
    try {
      const getPost = await readContract(config.config, {
        abi: postsContractABI.abi,
        address: postsContractAddress,
        functionName: 'posts',
        args: [postId],
      }) as string[];

      setPosts([
        {
          tokenId: postId.toString(),
          title: getPost[0],
          content: getPost[1],
          authorAddress: getPost[2],
          authorName: getPost[3],
          description: getPost[4],
        },
      ]);

      // Fetch list of submission IDs for the post
      const submissionIds = await readContract(config.config, {
        abi: postConsensusContractABI.abi,
        address: postConsensusContractAddress,
        functionName: 'getPostSubmissionList',
        args: [postId],
      }) as bigint[];


      const subs = submissionIds.map((id) => Number(id));
      const submissionDetails: SubmissionType[] = [];
      const map = new Map<string, number>();

      // Loop through each submission ID to fetch community name and other details
      for (const submissionId of subs) {
        const submissionData = await readContract(config.config, {
          abi: postConsensusContractABI.abi,
          address: postConsensusContractAddress,
          functionName: 'CommunitySubmissions',
          args: [submissionId.toString()], // Convert bigint to string
        }) as string[];
        const communityName = submissionData[0];
        map.set(communityName, submissionId); // Store community name with its ID
        console.log(submissionData)
        submissionDetails.push({
          id: submissionId,
          communityName: communityName,
          timestamp: Number(submissionData[1]),
          consensusTime: Number(submissionData[2]),
          consensusType: TypeMap[Number(submissionData[3])]
        });
      }

      setCommunityMap(map);
      setCommunitySubmissions(submissionDetails);
    } catch (error) {
      console.error('Error fetching post submissions:', error);
      setError('Failed to fetch post submissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviewsForSubmission = async (submissionId: number) => {
    setLoading(true);
    setError('');
    try {
      const reviewIds = await readContract(config.config, {
        abi: postConsensusContractABI.abi,
        address: postConsensusContractAddress,
        functionName: 'getReviewsForSubmission',
        args: [submissionId],
      }) as bigint[];

      const reviewsList: ReviewType[] = [];

      for (const id of reviewIds) {
        const reviewData = await readContract(config.config, {
          abi: postConsensusContractABI.abi,
          address: postConsensusContractAddress,
          functionName: 'reviews',
          args: [id.toString()],
        }) as string[];
        reviewsList.push({
            reviewId: Number(id),
            member: reviewData[0],             // Assuming 'member' is the address at index 0
            content: reviewData[1],            // 'content' of the review at index 1
            consensus: Number(reviewData[2]),  // Numeric 'consensus' at index 2
            consensusType: reviewData[3],      // 'consensusType' as string at index 3
            creds: Number(reviewData[4]),      // Credibility score as a number at index 4
            afterConsensus: reviewData[5] === 'true' ? 'Yes' : 'No', // Boolean 'afterConsensus' at index 5, converted to 'Yes'/'No'
          });
          
      }

      setReviews(reviewsList);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setError('Failed to fetch reviews. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getFormattedDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  return (
    <Container className="mt-5 d-flex flex-column align-items-center">
      <Card className="shadow-sm w-75">
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {loading ? (
            <Spinner animation="border" variant="info" />
          ) : posts.length > 0 ? (
            <Post post={posts[0]} />
          ) : (
            <Alert variant="warning" className="text-center">No post found with ID: {value}</Alert>
          )}

          {posts.length > 0 && (
            <div className="d-flex justify-content-center mt-3">
              <Button variant="info" onClick={() => setShowSubmissionModal(true)}>
                Submit Post to Community
              </Button>
            </div>
          )}

          {!error && communitySubmissions.length > 0 && (
            <>
              <Dropdown className="d-flex justify-content-center mt-3">
                <Dropdown.Toggle variant="info" id="dropdown-basic" >
                  {selectedSubmission ? selectedSubmission.communityName : "View Community Submissions"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {communitySubmissions.map((submission) => (
                    <Dropdown.Item
                      key={submission.id}
                      onClick={() => setSelectedSubmission(submission)}
                    >
                      {submission.communityName}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              {selectedSubmission && (
                <Card className="mt-3 shadow-sm">
                  <Card.Body className="text-center">
                    <p><strong>Community:</strong> {selectedSubmission.communityName}</p>
                    <p><strong>Consensus Type:</strong> {selectedSubmission.consensusType}</p>
                    <p><strong>Submitted:</strong> {getFormattedDate(selectedSubmission.timestamp)}</p>
                    <p><strong>Check Time:</strong> {getFormattedDate(selectedSubmission.timestamp + selectedSubmission.consensusTime)}</p>
                  </Card.Body>
                  
                  <button className="btn btn-success" onClick={() => window.open(`/search-results?type=Bounties&value=${selectedSubmission.id}`)}> View Bounties</button>

                  <Card.Body>
                    <h5 className="text-center">Reviews:</h5>
                    {reviews.length > 0 ? (
                      reviews.map((review) => (
                        <ReviewCard key={review.reviewId} review={review} />
                      ))
                    ) : (
                      <p className="text-center">No reviews found for this submission.</p>
                    )}
                    <div className="d-flex justify-content-center mt-3">
                      <Button variant="primary" onClick={() => setShowReviewModal(true)}>
                        Submit a Review
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
          {!error && !loading && communitySubmissions.length === 0 && posts.length > 0 && (
            <Alert variant="info" className="text-center">No community submissions found for this post.</Alert>
          )}
        </Card.Body>
      </Card>


      {/* Review Submission Modal */}
      <ReviewSubmissionModal
        show={showReviewModal}
        handleClose={() => setShowReviewModal(false)}
        communitySubmissionId={selectedSubmission ? String(selectedSubmission.id) : ''} // Convert ID to string or pass an empty string
        communityName={selectedSubmission?.communityName || ''} // Use empty string if undefined
        onSubmissionSuccess={() => setSelectedSubmission(null)}
      />

      {/* Post Submission Modal */}
      <PostSubmissionModal
        show={showSubmissionModal}
        handleClose={() => setShowSubmissionModal(false)}
        postId={value}
        onSubmissionSuccess={() => setSelectedSubmission(null)}
      />
    </Container>
  );
}

export default PostIdSearch;
